/**
 * Áudio full-duplex de uma ligação: microfone do operador → gateway → WhatsApp,
 * e a voz do cliente no caminho inverso.
 *
 * PCM s16le mono 16 kHz nos dois sentidos — o formato do codec MLOW, que o
 * gateway usa na perna do WhatsApp. O browser reamostra o microfone de graça
 * quando o `AudioContext` é criado com `sampleRate: 16000`.
 *
 * O whiz-server NÃO está neste caminho: o WebSocket vai direto ao gateway, com
 * o token que `/ligacoes/:idInbox/:idLigacao/sessao` emitiu.
 */

/** Amostras por frame (60 ms a 16 kHz) — o frame do MLOW. */
const FRAME_SAMPLES = 960;
const SAMPLE_RATE = 16000;

/**
 * Captura via AudioWorklet, não `createScriptProcessor`.
 *
 * O `ScriptProcessorNode` está depreciado e roda na MAIN THREAD: qualquer
 * re-render do Vue ou GC durante a ligação vira estalo no áudio de quem está do
 * outro lado. O worklet roda na thread de áudio e não é afetado.
 */
const WORKLET_SRC = `
class CapturaPCM extends AudioWorkletProcessor {
  process(inputs) {
    const canal = inputs[0] && inputs[0][0];
    // Sem entrada (mic mutado no nível do device) o worklet tem de continuar
    // vivo: devolver false o encerraria e não haveria como retomar.
    if (!canal) return true;
    const i16 = new Int16Array(canal.length);
    for (let i = 0; i < canal.length; i++) {
      // Clamp, nunca wrap: amostra acima de 1.0 tem de saturar, não inverter o
      // sinal e virar um estouro audível.
      let s = canal[i] * 32767;
      i16[i] = s > 32767 ? 32767 : s < -32768 ? -32768 : s;
    }
    this.port.postMessage(i16.buffer, [i16.buffer]);
    return true;
  }
}
registerProcessor('captura-pcm', CapturaPCM);
`;

/**
 * `pausado` = o AudioContext parou de rodar sem a ligação cair.
 *
 * É o que o iOS faz quando o app vai para segundo plano ou a tela bloqueia:
 * WebKit interrompe o contexto (estado `suspended`, ou o `interrupted` que só
 * o Safari tem), o AudioWorklet deixa de processar e nenhum frame é enviado.
 * O WebSocket continua aberto, então a chamada NÃO cai — o operador só some da
 * conversa. Sem este estado a tela seguia dizendo "Em ligação" enquanto o
 * microfone não mandava nada, que é a pior versão possível do problema.
 *
 * Não há conserto: captura de microfone em segundo plano não existe para
 * conteúdo web no iOS (só app nativo, com background mode de VoIP). O que dá
 * para fazer é dizer a verdade e retomar sozinho na volta.
 */
export type LigacaoAudioEstado = 'conectando' | 'ativo' | 'encerrado' | 'erro' | 'pausado';

export type LigacaoAudioOpcoes = {
    /** URL absoluta `wss://.../call/audio` devolvida por `sessao`, SEM o token. */
    url: string;
    /**
     * Token que autoriza este áudio, enviado como SUBPROTOCOLO do WebSocket
     * (header `Sec-WebSocket-Protocol`) e não na URL.
     *
     * O motivo é higiene de log: token em query string vai para o log de acesso
     * do proxy reverso (nginx registra a request line inteira por padrão) e
     * para o histórico do browser. Como este token É a autorização do stream de
     * áudio, mantê-lo fora de log importa mais que o check de origem.
     */
    token: string;
    onEstado?: (estado: LigacaoAudioEstado, detalhe?: string) => void;
    /** Nível de pico do microfone (0..1), para o medidor na tela. */
    onNivel?: (nivel: number) => void;
};

export class LigacaoAudio {
    private ws: WebSocket | null = null;
    private ac: AudioContext | null = null;
    private stream: MediaStream | null = null;
    private no: AudioWorkletNode | null = null;
    private fonte: MediaStreamAudioSourceNode | null = null;
    /** Instante agendado para o próximo buffer de reprodução (jitter buffer). */
    private proximo = 0;
    private mutado = false;
    /**
     * Reprodução suspensa (ligação EM ESPERA). Distinto de `mutado`, que é só o
     * microfone: com duas ligações ativas, sem suspender a saída as DUAS vozes
     * sairiam misturadas no fone do operador.
     *
     * O socket segue aberto e os frames seguem chegando — são descartados aqui.
     * Fechar o socket perderia a ligação, e é o oposto de "em espera".
     */
    private saidaSuspensa = false;
    private encerrado = false;
    /** Listener de visibilidade, guardado para poder remover no encerramento. */
    private aoVoltar: (() => void) | null = null;
    /**
     * Track de captura em uso. É ELA que diz se o operador está sendo ouvido —
     * o estado do `AudioContext` não diz.
     */
    private track: MediaStreamTrack | null = null;
    /**
     * Trava de tela durante a ligação — SÓ no iOS. Ver `precisaDeTravaDeTela`.
     */
    private travaDeTela: {
        release(): Promise<void>;
        addEventListener?(tipo: 'release', ouvinte: () => void): void;
    } | null = null;

    constructor(private readonly opcoes: LigacaoAudioOpcoes) {}

    get estaMutado(): boolean { return this.mutado; }
    get estaEmEspera(): boolean { return this.saidaSuspensa; }

    async iniciar(): Promise<void> {
        this.opcoes.onEstado?.('conectando');

        // `getUserMedia` exige contexto seguro (https ou localhost). Falhar aqui
        // com mensagem clara evita o clássico "a ligação não tem áudio".
        if (!navigator.mediaDevices?.getUserMedia) {
            this.falhar('Microfone indisponível: a página precisa estar em HTTPS.');
            return;
        }

        try {
            this.ac = new AudioContext({ sampleRate: SAMPLE_RATE });
            await this.ac.resume();
            this.vigiarInterrupcao();

            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
        } catch (e) {
            this.falhar(`Sem acesso ao microfone: ${(e as Error).message}`);
            return;
        }

        // O worklet é carregado de um blob para não depender de arquivo servido
        // separadamente (o build do Vite não copiaria um worklet solto).
        const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        try {
            await this.ac.audioWorklet.addModule(blobUrl);
        } catch (e) {
            this.falhar(`Falha ao carregar o processador de áudio: ${(e as Error).message}`);
            return;
        } finally {
            // Revogado assim que o módulo foi lido: mantê-lo vivo é vazamento.
            URL.revokeObjectURL(blobUrl);
        }

        this.abrirSocket();
        this.ligarCaptura();
        void this.travarTela();
    }

    /**
     * Observa o contexto parar e voltar.
     *
     * `onstatechange` é o sinal confiável: vale para o iOS indo a segundo plano,
     * para a tela bloqueando e para a interrupção por chamada telefônica nativa
     * — todos derrubam o contexto sem tocar no socket.
     */
    private vigiarInterrupcao(): void {
        if (!this.ac) return;

        this.ac.onstatechange = () => this.reavaliarEstado();

        if (typeof document === 'undefined') return;
        this.aoVoltar = () => {
            if (this.encerrado || document.visibilityState !== 'visible') return;
            // O contexto NÃO volta sozinho depois da interrupção do iOS: sem
            // este `resume` a ligação continuava muda mesmo com o app de volta
            // na frente, e só encerrar e refazer resolvia.
            void this.ac?.resume().then(() => this.recuperarCaptura()).catch(() => undefined);
            // A trava cai sozinha quando a aba perde a visibilidade; pedir de
            // volta é responsabilidade de quem a pediu.
            void this.travarTela();
        };
        document.addEventListener('visibilitychange', this.aoVoltar);
    }

    /**
     * Passa a escutar a track de captura.
     *
     * Vigiar só o `AudioContext` era o erro da primeira versão: no iOS o segundo
     * plano SILENCIA a track (`muted = true`), e outro app tomando o microfone
     * pode ENCERRÁ-LA. Nos dois casos o contexto volta a `running` no retorno, o
     * card anunciava "Em ligação" com medidor animado, e o operador seguia mudo.
     * Mentir com confiança é pior que o silêncio original.
     */
    private vigiarTrack(): void {
        this.track = this.stream?.getAudioTracks()[0] ?? null;
        if (!this.track) return;

        this.track.onmute = () => this.reavaliarEstado();
        this.track.onunmute = () => this.reavaliarEstado();
        // `ended` não volta sozinho: a track morreu e o nó de origem precisa ser
        // reconstruído sobre um microfone novo.
        this.track.onended = () => {
            this.opcoes.onEstado?.('pausado');
            void this.recuperarCaptura();
        };
    }

    /**
     * Juiz único do estado. Três condições precisam valer AO MESMO TEMPO para o
     * operador estar sendo ouvido: socket aberto, contexto rodando, track viva e
     * não silenciada. Qualquer uma sozinha mente.
     */
    private reavaliarEstado(): void {
        if (this.encerrado) return;

        const contextoOk = this.ac?.state === 'running';
        const trackOk = !!this.track && this.track.readyState === 'live' && !this.track.muted;
        const socketOk = this.ws?.readyState === WebSocket.OPEN;

        if (contextoOk && trackOk && socketOk) {
            // Ressincroniza o jitter buffer: `proximo` ficou parado no passado
            // enquanto o contexto dormia.
            if (this.ac) this.proximo = this.ac.currentTime;
            this.opcoes.onEstado?.('ativo');
            return;
        }
        // Socket fechado é `encerrado`, não `pausado`: não há o que retomar.
        if (!socketOk) return;
        this.opcoes.onEstado?.('pausado');
    }

    /**
     * Refaz a captura quando a track morreu.
     *
     * `resume()` sozinho devolve a REPRODUÇÃO; a captura sobre uma track
     * encerrada é muda para sempre, porque `createMediaStreamSource` ficou preso
     * a ela. Então pede microfone de novo e religa a origem no mesmo worklet.
     */
    private async recuperarCaptura(): Promise<void> {
        if (this.encerrado || !this.ac || !this.no) return;
        if (this.track && this.track.readyState === 'live') {
            this.reavaliarEstado();
            return;
        }

        try {
            const novo = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            if (this.encerrado) {
                novo.getTracks().forEach(t => t.stop());
                return;
            }

            this.stream?.getTracks().forEach(t => t.stop());
            try { this.fonte?.disconnect(); } catch { /* já desconectado */ }

            this.stream = novo;
            this.fonte = this.ac.createMediaStreamSource(novo);
            this.fonte.connect(this.no);
            this.vigiarTrack();
            this.reavaliarEstado();
        } catch (e) {
            this.opcoes.onEstado?.('erro', `Não foi possível retomar o microfone: ${(e as Error).message}`);
        }
    }

    /**
     * A trava de tela só vale a pena onde a tela apagando MATA o áudio, e isso
     * é específico do iOS.
     *
     * No Android o Chrome mantém a captura viva com o app em segundo plano e
     * com a tela apagada — abas que seguram microfone são isentas do
     * congelamento de aba. Lá a trava não conserta nada e ainda faz mal: a tela
     * acesa encostada na orelha gasta bateria e convida toque acidental, e o
     * alvo mais provável do rosto é justamente o botão vermelho de encerrar
     * (não existe sensor de proximidade para conteúdo web).
     *
     * User agent aqui é último recurso consciente: `useDevice.ts` prefere
     * capacidade a UA, mas não existe feature detection para "este sistema
     * interrompe o AudioContext em segundo plano". É um bug de plataforma, e o
     * jeito de reconhecer a plataforma é o UA.
     */
    private precisaDeTravaDeTela(): boolean {
        if (typeof navigator === 'undefined') return false;
        const ua = navigator.userAgent;
        // iPadOS moderno se anuncia como Mac; `maxTouchPoints` o denuncia.
        return /iPad|iPhone|iPod/.test(ua)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    /** Pede a trava de tela. Silenciosa onde não existe (iOS < 16.4, Firefox). */
    private async travarTela(): Promise<void> {
        if (this.encerrado || this.travaDeTela) return;
        if (!this.precisaDeTravaDeTela()) return;
        const nav = navigator as Navigator & {
            wakeLock?: {
                request(tipo: 'screen'): Promise<{
                    release(): Promise<void>;
                    addEventListener?(tipo: 'release', ouvinte: () => void): void;
                }>;
            };
        };
        if (!nav.wakeLock) return;
        try {
            const sentinela = await nav.wakeLock.request('screen');
            // Encerrou durante o pedido (desligou logo depois de atender):
            // guardar não adianta, ninguém mais libera, e a tela do operador
            // ficaria sem bloquear pelo resto da sessão.
            if (this.encerrado) {
                void sentinela.release().catch(() => undefined);
                return;
            }
            // O sistema LIBERA a trava sozinho quando a aba deixa de ficar
            // visível. Sem zerar a referência, o guard de entrada recusava todo
            // pedido seguinte — e a trava morria no primeiro retorno, justo o
            // ciclo para o qual ela existe.
            sentinela.addEventListener?.('release', () => { this.travaDeTela = null; });
            this.travaDeTela = sentinela;
        } catch {
            // Aba em segundo plano, bateria baixa, política do SO: a trava é
            // conforto e nunca pode derrubar a ligação.
        }
    }

    private abrirSocket(): void {
        // O token vai como subprotocolo. O servidor DEVE ecoá-lo de volta, senão
        // o browser derruba a conexão: pela RFC 6455 um cliente que ofereceu
        // subprotocolos pode rejeitar servidor que não selecione nenhum.
        // `client=web`: o gateway (whiz-wa-go) usa para separar web de mobile nas
        // métricas do áudio e para dar ao web — que não reconecta — uma
        // tolerância de ping maior (um soluço de rede não pode derrubar o áudio).
        const separador = this.opcoes.url.includes('?') ? '&' : '?';
        const ws = new WebSocket(`${this.opcoes.url}${separador}client=web`, [this.opcoes.token]);
        ws.binaryType = 'arraybuffer';
        this.ws = ws;

        ws.onopen = () => {
            this.proximo = this.ac?.currentTime ?? 0;
            // Pelo juiz único: socket aberto não basta para dizer "ativo" se o
            // microfone estiver silenciado ou o contexto suspenso.
            this.reavaliarEstado();
        };
        ws.onmessage = ev => this.reproduzir(ev.data as ArrayBuffer);
        ws.onerror = () => this.falhar('Erro no canal de áudio.');
        ws.onclose = () => {
            if (this.encerrado) return;
            this.opcoes.onEstado?.('encerrado');
            // Desmonta TUDO, não só os listeners. Sem áudio não há o que
            // retomar, e isto fecha dois buracos de uma vez: a sessão deixa de
            // poder ser ressuscitada por um alt-tab (`visibilitychange` →
            // `resume` → "Em ligação" sobre socket fechado), e o microfone é
            // solto de verdade. Marcar só `encerrado` aqui era pior que o bug
            // original: `encerrar()` sai cedo quando a flag já está de pé, então
            // as tracks nunca parariam e o browser seguiria mostrando "esta aba
            // está usando seu microfone" pelo resto da sessão.
            this.encerrar();
        };
    }

    private ligarCaptura(): void {
        if (!this.ac || !this.stream) return;
        this.fonte = this.ac.createMediaStreamSource(this.stream);
        this.no = new AudioWorkletNode(this.ac, 'captura-pcm');

        this.no.port.onmessage = ev => {
            if (this.mutado || this.ws?.readyState !== WebSocket.OPEN) return;
            const buf = ev.data as ArrayBuffer;
            this.ws.send(buf);
            if (this.opcoes.onNivel) this.opcoes.onNivel(pico(new Int16Array(buf)));
        };

        this.fonte.connect(this.no);
        this.vigiarTrack();
        // O worklet NÃO é conectado ao destino: ligá-lo à saída reproduziria o
        // próprio microfone no fone do operador (eco local). Ele existe só para
        // capturar, e um AudioWorkletNode sem destino segue processando.
    }

    /**
     * Reprodução com jitter buffer simples: cada frame é agendado no fim do
     * anterior. Quando o relógio já passou do agendamento (rede engasgou), o
     * ponteiro é ressincronizado com uma folga de um frame em vez de despejar
     * tudo de uma vez.
     */
    private reproduzir(dados: ArrayBuffer): void {
        if (!this.ac || this.saidaSuspensa) return;
        const i16 = new Int16Array(dados);
        if (i16.length === 0) return;

        const f32 = new Float32Array(i16.length);
        // Divide por 32768 (não 32767) para o decodificado ficar dentro de [-1,1).
        for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;

        const buffer = this.ac.createBuffer(1, f32.length, SAMPLE_RATE);
        buffer.copyToChannel(f32, 0);
        const no = this.ac.createBufferSource();
        no.buffer = buffer;
        no.connect(this.ac.destination);

        const agora = this.ac.currentTime;
        if (this.proximo < agora) this.proximo = agora + FRAME_SAMPLES / SAMPLE_RATE;
        no.start(this.proximo);
        this.proximo += buffer.duration;
    }

    mutar(valor: boolean): void { this.mutado = valor; }

    /**
     * Põe a ligação em espera: suspende a reprodução E o microfone, sem fechar o
     * socket. Retomar precisa ressincronizar o ponteiro do jitter buffer —
     * `proximo` ficou parado no passado e, sem isto, o primeiro lote de frames
     * seria agendado todo de uma vez (rajada audível).
     */
    espera(valor: boolean): void {
        this.saidaSuspensa = valor;
        this.mutado = valor;
        if (!valor && this.ac) this.proximo = this.ac.currentTime;
    }

    private falhar(detalhe: string): void {
        this.opcoes.onEstado?.('erro', detalhe);
        this.encerrar();
    }

    /**
     * Solta listeners e a trava de tela. Separado de `encerrar` porque o
     * `onclose` do socket precisa da mesma limpeza sem derrubar o resto.
     */
    private soltarVigilancia(): void {
        if (this.aoVoltar && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.aoVoltar);
        }
        this.aoVoltar = null;
        if (this.ac) this.ac.onstatechange = null;
        if (this.track) {
            this.track.onmute = null;
            this.track.onunmute = null;
            this.track.onended = null;
        }
        void this.travaDeTela?.release().catch(() => undefined);
        this.travaDeTela = null;
    }

    /** Idempotente: o encerramento chega tanto pelo botão quanto pelo `onclose`. */
    encerrar(): void {
        if (this.encerrado) return;
        this.encerrado = true;

        this.soltarVigilancia();

        try { this.no?.port.close(); } catch { /* já fechado */ }
        try { this.no?.disconnect(); } catch { /* já desconectado */ }
        try { this.fonte?.disconnect(); } catch { /* já desconectado */ }
        // Parar as tracks é o que apaga o indicador de microfone do browser —
        // sem isto o operador continua vendo "esta aba está usando seu microfone".
        this.stream?.getTracks().forEach(t => t.stop());
        try { this.ws?.close(); } catch { /* já fechado */ }
        void this.ac?.close().catch(() => undefined);

        this.no = null;
        this.fonte = null;
        this.stream = null;
        this.track = null;
        this.ws = null;
        this.ac = null;
    }
}

function pico(amostras: Int16Array): number {
    let max = 0;
    for (let i = 0; i < amostras.length; i++) {
        const v = Math.abs(amostras[i]);
        if (v > max) max = v;
    }
    return max / 32768;
}
